import React, { useMemo, useRef, useState } from 'react';
import {
  Animated,
  Image,
  PanResponder,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

const alertIcon = require('assets/alert.svg');
const warningIcon = require('assets/warning.svg');
const closeIcon = require('assets/close.svg');

function Icon({ level }) {
  if (level === 1) {
    return <Image source={warningIcon} style={styles.icon} />;
  }

  return <Image source={alertIcon} style={styles.icon} />;
}

export function Notification({ data, dismiss }) {
  const { id, title, description, level = 0 } = data;
  const [position, setPosition] = useState(null);
  const [height, setHeight] = useState(0);
  const pan = useRef(new Animated.ValueXY()).current;
  const initialAnim = useRef(new Animated.Value(10)).current;

  function close() {
    Animated.timing(initialAnim, {
      toValue: -height + 10,
      duration: 300,
      useNativeDriver: false,
    }).start();
    setTimeout(() => dismiss(), 300);
  }

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: () => true,
        onPanResponderMove: Animated.event([null, { dx: pan.x, dy: pan.y }], {
          useNativeDriver: false,
        }),
        onPanResponderRelease: () => {
          if (height > 0 && position._value < -height) {
            dismiss();
          }
          pan.extractOffset();
        },
      }),
    [height, position, pan, dismiss],
  );
  pan.y.addListener((e) => {
    if (e.value >= 0) {
      setPosition(new Animated.Value(0));
      pan.setOffset({ x: 0, y: 0 });
    } else {
      setPosition(pan.y);
    }
  });

  return (
    <Animated.View
      testID={'notification'}
      key={id}
      style={[
        styles.container,
        {
          transform: [{ translateY: initialAnim }],
        },
        position
          ? {
              transform: [{ translateY: position }],
            }
          : {},
      ]}
      onLayout={(event) => {
        const { height: h } = event.nativeEvent.layout;
        setHeight(h);
        Animated.timing(initialAnim, {
          toValue: 10,
          duration: 300,
          useNativeDriver: false,
        }).start();
        pan.setOffset({ x: 0, y: 10 });
      }}
    >
      <Animated.View {...panResponder.panHandlers}>
        <View style={styles.innerContainer}>
          <Icon level={level} />
          <View style={styles.textContainer}>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.text}>{description}</Text>
          </View>
          <Pressable onPress={close} style={styles.closeIconContainer}>
            <Image source={closeIcon} style={styles.closeIcon} />
          </Pressable>
        </View>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    zIndex: 9999,
    width: '100%',
    padding: 10,
  },
  innerContainer: {
    backgroundColor: '#fff',
    borderRadius: 10,
    width: '100%',
    padding: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    boxShadowRadius: 5,
    boxShadowOpacity: 0.1,
  },
  textContainer: {
    display: 'flex',
    paddingLeft: 10,
    flex: 1,
  },
  title: {
    fontSize: 20,
  },
  text: {
    fontSize: 15,
  },
  icon: {
    width: 40,
    height: 40,
  },
  closeIconContainer: {
    alignSelf: 'flex-start',
  },
  closeIcon: {
    width: 30,
    height: 30,
  },
});
